import { useEffect, useState, useRef } from "react";
import api, { formatDate } from "@/lib/api";
import { ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";

const BRAND_DOT = {
  PPD_expertise: "bg-amber-400",       // 🟨 Expertise PPD
  PPD_coulisses: "bg-orange-500",      // 🟧 Coulisses / retour d'expérience PPD
  FRANCHIMOB: "bg-blue-500",           // 🟦 Franchi'Mob
};

const EXPERTISE_TYPES = ["franchise", "concept", "standardisation", "capex", "rentabilite", "erreurs"];

function classifyDot(item) {
  if (item.brand === "FRANCHIMOB") return "FRANCHIMOB";
  if (item.publication_type === "retour_experience" || item.publication_type === "coulisses") return "PPD_coulisses";
  if (EXPERTISE_TYPES.includes(item.publication_type)) return "PPD_expertise";
  return "PPD_expertise";  // default for PPD
}

const STATUS_LABEL = {
  idee: "Idée", a_developper: "À développer", brouillon: "Brouillon", a_relire: "À relire",
  valide: "Validé", a_publier: "Programmé", publie: "Publié",
};

export default function MiniCalendar({ onDayClick, onIdeaClick, onIdeaDrop }) {
  const [data, setData] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const hoverPos = useRef({ x: 0, y: 0 });

  const load = async () => {
    const { data: d } = await api.get("/ideas/calendar/month", { params: cursor });
    setData(d);
  };
  useEffect(() => { load(); }, [cursor]);

  // Refresh on parent changes
  useEffect(() => { window.addEventListener("ideas:refresh", load); return () => window.removeEventListener("ideas:refresh", load); }, [cursor]);

  if (!data) return <div className="card-flat p-5 text-sm text-stone-500">Chargement…</div>;

  const moveMonth = (delta) => {
    setCursor(({ year, month }) => {
      let m = month + delta;
      let y = year;
      if (m > 12) { m = 1; y++; }
      if (m < 1) { m = 12; y--; }
      return { year: y, month: m };
    });
  };

  const firstDay = new Date(cursor.year, cursor.month - 1, 1);
  const daysInMonth = new Date(cursor.year, cursor.month, 0).getDate();
  const startWeekday = (firstDay.getDay() + 6) % 7; // 0 = Monday
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const todayIso = new Date().toISOString().slice(0, 10);

  const onDrop = async (dayIso, e) => {
    e.preventDefault();
    setDragOver(null);
    const ideaId = e.dataTransfer.getData("text/idea-id");
    if (ideaId && onIdeaDrop) {
      await onIdeaDrop(ideaId, dayIso);
      load();
    }
  };

  return (
    <div className="card-flat p-4 relative" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <button data-testid="cal-prev" onClick={() => moveMonth(-1)} className="p-1 rounded hover:bg-stone-100"><ChevronLeft size={16}/></button>
        <div className="font-display text-base font-bold capitalize">{monthLabel}</div>
        <button data-testid="cal-next" onClick={() => moveMonth(1)} className="p-1 rounded hover:bg-stone-100"><ChevronRight size={16}/></button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-1">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => <div key={`dow-${i}-${d}`}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} className="aspect-square"/>;
          const dayIso = `${cursor.year}-${String(cursor.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const items = data.days[dayIso] || [];
          const isToday = dayIso === todayIso;
          const isDragOver = dragOver === dayIso;
          return (
            <div
              key={dayIso}
              data-testid={`cal-day-${dayIso}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(dayIso); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(dayIso, e)}
              onClick={() => onDayClick?.(dayIso, items)}
              className={`aspect-square rounded border ${isToday ? "border-stone-900 font-bold" : "border-stone-100"} ${isDragOver ? "bg-[var(--pink-powder)]/60 border-[var(--ppd)]" : "hover:bg-stone-50"} flex flex-col items-center justify-start p-1 cursor-pointer relative`}
            >
              <div className="text-[11px] text-stone-700">{day}</div>
              {items.length > 0 && (
                <div className="flex flex-wrap gap-0.5 justify-center mt-0.5">
                  {items.slice(0, 3).map((it) => (
                    <span
                      key={it.id}
                      onMouseEnter={(e) => { hoverPos.current = { x: e.clientX, y: e.clientY }; setHoveredItem(it); }}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={(e) => { e.stopPropagation(); onIdeaClick?.(it.id); }}
                      className={`w-2 h-2 rounded-full ${BRAND_DOT[classifyDot(it)]} ${it.status === "publie" ? "ring-2 ring-emerald-400" : ""}`}
                      data-testid={`cal-dot-${it.id}`}
                    />
                  ))}
                  {items.length > 3 && <span className="text-[8px] text-stone-500">+{items.length - 3}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-3 text-[10px] text-stone-600">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"/> Franchi'Mob</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"/> Expertise PPD</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"/> Coulisses / Retour d'expérience</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-stone-200 ring-2 ring-emerald-400"/> Publié</span>
      </div>

      {/* Tooltip */}
      {hoveredItem && (
        <div
          className="fixed z-50 bg-stone-900 text-white text-xs rounded-lg shadow-xl p-3 max-w-xs pointer-events-none"
          style={{ left: hoverPos.current.x + 12, top: hoverPos.current.y + 12 }}
        >
          <div className="font-semibold mb-1">{hoveredItem.title}</div>
          <div className="text-stone-300">{formatDate(hoveredItem.date)} · {STATUS_LABEL[hoveredItem.status] || hoveredItem.status}</div>
          {hoveredItem.publication_type && <div className="text-stone-400 mt-1">{hoveredItem.publication_type}</div>}
        </div>
      )}
    </div>
  );
}

export function CalendarKpis({ data }) {
  if (!data) return null;
  const k = data.kpis;
  const Item = ({ label, value, target, accent }) => (
    <div className="flex items-baseline gap-2 text-sm" data-testid={`cal-kpi-${label}`}>
      <span className={`text-[10px] uppercase tracking-widest font-bold ${accent}`}>{label}</span>
      <span className="font-mono font-bold">{value}/{target}</span>
    </div>
  );
  return (
    <div className="card-flat p-4 space-y-3" data-testid="cal-kpis">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Cette semaine</div>
        <div className="flex flex-col gap-1.5">
          <Item label="PPD" value={k.week_ppd} target={k.week_ppd_target} accent="text-amber-700"/>
          <Item label="Franchi'Mob" value={k.week_fmob} target={k.week_fmob_target} accent="text-blue-700"/>
        </div>
      </div>
      <div className="pt-3 border-t border-stone-100">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Ce mois</div>
        <div className="flex flex-col gap-1.5">
          <Item label="PPD" value={k.month_ppd} target={k.month_ppd_target} accent="text-amber-700"/>
          <Item label="Franchi'Mob" value={k.month_fmob} target={k.month_fmob_target} accent="text-blue-700"/>
        </div>
      </div>
      {data.alerts?.length > 0 && (
        <div className="pt-3 border-t border-stone-100 space-y-1.5" data-testid="cal-alerts">
          {data.alerts.map((a, i) => (
            <div key={`alert-${i}-${a.label || ""}`} className={`text-xs flex items-start gap-1.5 ${a.level === "rouge" ? "text-[var(--ppd)]" : "text-amber-700"}`}>
              <AlertCircle size={12} className="mt-0.5 shrink-0"/> {a.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
